import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMerchantMap } from '@/hooks';

interface MerchantCellProps {
  merchantId: string;
}

/**
 * Tabellenzelle die Merchant-Name + Customer ID anzeigt.
 * Name ist klickbar und navigiert zur Merchant-Detail-Seite.
 * Resolved Name und Customer ID intern ueber useMerchantMap().
 *
 * Die interne merchantId wird hier NICHT mehr angezeigt — sie ist eine
 * Mapping-Referenz und gehoert nicht in Kundenansichten (Issue #310).
 *
 * Fallback-Kette (nie leere Zelle):
 *   1. Name vorhanden  → Zeile 1 = Name (Link), Zeile 2 = Customer ID (falls da).
 *   2. nur Customer ID → Zeile 1 = Customer ID als Primaer-Referenz.
 *   3. weder noch      → Zeile 1 = merchantId (letzter Anker).
 */
export function MerchantCell({ merchantId }: MerchantCellProps) {
  const { t } = useTranslation('common');
  const { resolveMerchantName, resolveCustomerId } = useMerchantMap();

  const resolvedName = resolveMerchantName(merchantId);
  const hasName = resolvedName !== merchantId;
  const customerId = resolveCustomerId(merchantId);

  const customerIdLine = customerId && (
    <div className="text-xs text-muted-foreground">
      {t('merchantCell.customerIdPrefix')} {customerId}
    </div>
  );

  // 1. Name vorhanden: Link + Customer ID darunter.
  if (hasName) {
    return (
      <div>
        <Link
          to={`/merchants/${merchantId}`}
          className="font-medium text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {resolvedName}
        </Link>
        {customerIdLine}
      </div>
    );
  }

  // 2. Kein Name, aber Customer ID: Customer ID als Primaer-Referenz.
  if (customerId) {
    return (
      <div className="font-medium">
        {t('merchantCell.customerIdPrefix')} {customerId}
      </div>
    );
  }

  // 3. Letzter Anker: merchantId.
  return <div className="font-mono text-xs text-muted-foreground">{merchantId}</div>;
}
